import { useState } from 'react';

import {
  Badge,
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@@/ui/primitives';
import { ChevronsUpDown, Check, Loader2, CircleAlertIcon } from 'lucide-react';
import { cn } from '@@/lib/utils';
import { useSuggestions } from '../../../hooks/use-suggestions';
import { ConnectResourceType } from '../../../api/api-entities';
import { ConnectResourceValue, getDisplayName, getArn } from '../../../common/connect-resource';

export type { ConnectResourceValue };

interface ConnectResourceInputProps {
  resourceType: ConnectResourceType;
  value: ConnectResourceValue | null;
  onChange: (value: ConnectResourceValue) => void;
  disabled?: boolean;
  label?: string;
  hasError?: boolean;
}

export function ConnectResourceInput({
  resourceType,
  value,
  onChange,
  disabled = false,
  label,
  hasError = false
}: ConnectResourceInputProps) {
  const [open, setOpen] = useState(false);
  const [searchPrefix, setSearch] = useState('');

  const { data, isLoading, error } = useSuggestions({
    type: resourceType,
    namePrefix: searchPrefix.length >= 2 && searchPrefix.length <= 25 ? searchPrefix : undefined
  });

  const handleSelect = (resourceArn: string) => {
    const selectedResource = data?.resources.find((r) => r.arn === resourceArn);
    if (selectedResource) {
      onChange({
        connect_resource: {
          name: selectedResource.name,
          arn: selectedResource.arn,
          type: resourceType
        }
      });
      setOpen(false);
    }
  };

  const displayName = getDisplayName(value);
  const currentArn = getArn(value);
  const buttonText = displayName || 'Search...';

  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-2'>
        {label && <Label className='text-neutral-800 text-sm font-medium'>{label}</Label>}
        <Badge variant='outline' className='text-slate-800 border-slate-400 text-xs!'>
          {resourceType === ConnectResourceType.QUEUE && 'Connect Queue'}
          {resourceType === ConnectResourceType.CONTACT_FLOW && 'Connect Flow'}
          {resourceType === ConnectResourceType.CONTACT_FLOW_MODULE && 'Connect Flow Module'}
          {resourceType === ConnectResourceType.PROMPT && 'Connect Prompt'}
          {resourceType === ConnectResourceType.PHONE_NUMBER && 'Connect Phone Number'}
        </Badge>
      </div>
      <Popover onOpenChange={setOpen} open={open} modal={true}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={cn('justify-between text-sm! w-full', hasError && 'border-red-500 text-red-700')}
            role='combobox'
            variant='outline'
            disabled={disabled}
          >
            {buttonText}
            <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0 font-sans w-96' align='start'>
          <Command shouldFilter={false}>
            <CommandInput
              onValueChange={setSearch}
              className='text-sm!'
              placeholder='Type to search...'
              value={searchPrefix}
            />
            <CommandList>
              {error && (
                <div className='flex items-center p-2 px-3 text-sm text-amber-800'>
                  <CircleAlertIcon className='size-4 inline-block mr-2' />
                  Unable to load resources
                </div>
              )}
              {isLoading ? (
                <div className='flex items-center justify-center p-4'>
                  <Loader2 className='size-4 animate-spin' />
                  <span className='ml-2 text-muted-foreground text-sm'>Searching...</span>
                </div>
              ) : (
                <>
                  {data && data.resources && data.resources.length > 0 && (
                    <CommandGroup>
                      {data.resources.map((result) => {
                        const isSelected = currentArn === result.arn;
                        return (
                          <CommandItem key={result.arn} onSelect={() => handleSelect(result.arn)} value={result.name}>
                            <div className='flex flex-col w-full max-w-80'>
                              <span className='font-medium truncate'>{result.name}</span>
                              <span className='text-xs text-neutral-500 font-mono truncate'>{result.arn}</span>
                            </div>
                            <Check className={cn('mr-2 size-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
